<template>
  <div class="form-date-picker">
    <div
      role="group"
      class="form-group"
      :class="{
        'disabled': $attrs.disabled === ''
      }"
    >
      <label
        v-if="label"
        :for="$attrs.id"
      >{{ label }}</label>
      <div class="input-group">
        <Datepicker
          ref="datePicker"
          :value="parsedDate"
          :monday-first="true"
          :typeable="true"
          :format="customFormatter"
          :language="language"
          :disabled-dates="disabledDates"
          input-class="form-control"
          v-bind="$attrs"
          @selected="selected"
          v-on="listeners"
        />

        <div
          class="input-group-append"
          @click="showCalendar"
        >
          <SvgIcon
            name="components/date"
            width="16"
            height="16"
          />
        </div>
      </div>

      <div
        v-if="errorMessage"
        tabindex="-1"
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
        class="invalid-feedback"
      >
        {{ errorMessage }}
      </div>
    </div>
  </div>
</template>

<script>
import {format, parse} from 'date-fns'
import Datepicker from 'vuejs-datepicker'
import language from 'vuejs-datepicker/dist/locale/translations/en'
import SvgIcon from 'vue-svgicon/dist/components/svgicon.common'

import '../../icons/components/date'

language.monthsAbbr = language.months
language.days = ['S', 'M', 'T', 'W', 'T', 'F', 'S']

export default {
  components: {
    Datepicker,
    SvgIcon,
  },
  inheritAttrs: false,

  props: {
    /**
     * The current value of the textarea. Result will always be a string.
     */
    value: {
      type: [String, Date],
      default: undefined,
    },

    /**
     * Text to place in the label/legend of the form group
     */
    label: {
      type: String,
      default: undefined,
    },

    /**
     * Minimum date allowed to be selected
     */
    minDate: {
      type: Date,
      default: undefined,
    },

    /**
     * Maximum date allowed to be selected
     */
    maxDate: {
      type: Date,
      default: undefined,
    },

    /**
     * Maximum date allowed to be selected. Accepted patterns are listed here https://date-fns.org/v2.8.1/docs/format.
     */
    format: {
      type: String,
      default: 'dd-MM-yyyy',
    },

    /**
     * Controls the validation state appearance of the component. 'true' for valid, 'false' or 'null' for no validation state
     */
    isInvalid: {
      type: Boolean,
      default: false,
    },

    /**
     * Text to show when the form group has an invalid state
     */
    errorMessage: {
      type: String,
      default: undefined,
    },
  },

  data: () => ({
    language,
  }),

  computed: {
    state () {
      if (this.errorMessage && this.errorMessage.length > 0) {
        return false
      } else if (this.isInvalid) {
        return false
      } else {
        return null
      }
    },
    listeners () {
      const {input, cleared, selected, ...listeners} = this.$listeners
      return listeners
    },
    parsedDate () {
      try {
        if (typeof this.value === 'object') {
          return this.value
        } else {
          const parsedDate = parse(this.value, this.format, new Date())
          return parsedDate
        }
      } catch (e) {
        console.error(e)
        return new Date()
      }
    },
    disabledDates () {
      if (this.maxDate || this.minDate) {
        return {
          to: this.minDate,
          from: this.maxDate,
        }
      } else {
        return {}
      }
    },
  },
  methods: {
    selected (date) {
      // Hack this bug https://github.com/charliekassel/vuejs-datepicker/issues/733 from
      // the external component
      const selectedDate = date || this.parsedDate

      this.$emit('selected', selectedDate)
      this.$emit('input', selectedDate)

      if (!date && selectedDate) {
        this.$refs.datePicker.setValue(selectedDate)
      }

      if (!selectedDate) {
        this.$emit('cleared')
      }
    },
    customFormatter (date) {
      return format(date, this.format)
    },
    showCalendar () {
      this.$refs.datePicker.showCalendar()
    },
  },
}
</script>

<style lang="scss">


.form-date-picker {
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 0;

  &.disabled {
    pointer-events: none;
  }

  .vdp-datepicker {
    position: relative;
    flex: 1 1 0%;
    min-width: 0;
    margin-bottom: 0;
  }

  .vdp-datepicker__calendar {
    border-radius: 2px;
    background-color: #fff;
    border: none;
    color: $gray-900;
    font-size: $font-size-sm;
    padding: 0.5rem;
    box-shadow: 0 2px 8px 0 rgba(37, 37, 37, 0.16), 0 0 4px 0 rgba(37, 37, 37, 0.08);

    .day__month_btn,
    .month__year_btn {
      font-weight: 500;
      border-radius: 2px;

      &:hover:not(.disabled) {
        color: $cyan-700;
        background-color: $cyan-100;
      }
    }

    .cell {
      padding: 0;
      border-radius: 2px;
      border-color: transparent !important;

      &.day-header {
        font-size: $font-size-sm;
        color: $gray-800;
        line-height: 1.43;
        display: inline-flex;
        align-items: center;
        justify-content: center;
      }

      &.day,
      &.month,
      &.year {
        &:hover:not(.blank):not(.disabled):not(.selected) {
          color: $cyan-700;
          background-color: $cyan-100;
        }

        &:focus {
          border-color: $cyan-500 !important;
          border-width: 2px;
          outline: none;
        }

        &.selected {
          color: #fff;
          background-color: $cyan-700;
        }

        &.disabled {
          color: $gray-300;
        }
      }
    }

    header {
      .next,
      .prev {
        &:after {
          content: none;
        }

        &:before {
          display: block;
          content: ' ';
          background-size: 16px 16px;
          height: 16px;
          width: 16px;
          position: absolute;
        }

        &.disabled {
          opacity: 0.4;
        }
      }

      .prev {
        &:before {
          background-image: url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxNiIgaGVpZ2h0PSIxNiIgZmlsbD0ibm9uZSIgdmlld0JveD0iMCAwIDE2IDE2Ij4KICAgIDxwYXRoIGZpbGw9IiMyNDI0MjQiIGQ9Ik05IDEyTDUgOGw0LTQgMSAxLTMgMyAzIDMtMSAxeiIvPgo8L3N2Zz4K');
          right: 12px;
          top: 12px;
        }
      }

      .next {
        &:before {
          background-image: url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxNiIgaGVpZ2h0PSIxNiIgZmlsbD0ibm9uZSIgdmlld0JveD0iMCAwIDE2IDE2Ij4KICAgIDxwYXRoIGZpbGw9IiMyNDI0MjQiIGQ9Ik03IDRsNCA0LTQgNC0xLTEgMy0zLTMtMyAxLTF6Ii8+Cjwvc3ZnPgo=');
          right: 12px;
          top: 12px;
        }
      }
    }
  }

  .form-control {
    border-right: none !important;

    &:disabled {
      pointer-events: none;
    }

    &:hover:not(.is-invalid):not(:invalid) {
      border-color: transparent;
    }

    &:focus {
      border-color: $gray-200;
      padding: $input-padding-y $input-padding-x;
      border-width: 1px;
    }

    &[readonly] {
      ~ .input-group-append {
        background-color: $gray-000;
      }
    }
  }

  &:hover {
    .form-control,
    .form-control:focus {
      border-color: $gray-700 !important;
    }

    .input-group-append {
      border-color: $gray-700;
      color: $gray-800;
    }
  }

  .input-group-append {
    color: $gray-300;
    border: 1px solid $gray-200;
    border-left: none;
    padding: 0 1rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;

    border-top-right-radius: $btn-border-radius;
    border-bottom-right-radius: $btn-border-radius;

    @include transition($input-transition);

    &:focus,
    &.focus {
      outline: 0;
    }

    // Disabled comes first so active can properly restyle
    &.disabled,
    &:disabled {
      color: $gray-300;
      background-color: $gray-100;
      border-color: $gray-200;
      cursor: default;
    }
  }
}
</style>
